import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

const ParticleSystem = () => {
  const containerRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, z: 0 });

  useEffect(() => {
    // Optimized fragment shader
    const fragmentShader = `
    varying vec3 vColor;
    varying float vLife;
    
    void main() {
      vec2 coord = gl_PointCoord - vec2(0.5);
      float dist = length(coord);
      float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
      gl_FragColor = vec4(vColor, alpha * 0.8);
    }
  `;

    // Simple vertex shader (physics on CPU, rendering on GPU)
    const vertexShader = `
    attribute float size;
    attribute float life;
    
    varying vec3 vColor;
    varying float vLife;
    
    void main() {
      vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
      gl_Position = projectionMatrix * mvPosition;
      gl_PointSize = size * (600.0 / max(-mvPosition.z, 0.1));
      vColor = vec3(1.0, 1.0, 1.0);
      vLife = life;
    }
  `;
    if (!containerRef.current) return;

    let scene, camera, renderer;
    let composer = null;
    let particleSystem, material, geometry;
    let blackHole = null;
    let shouldShowBlackHole = false;
    let controls = null;
    let animationId;
    // Adaptive particle count based on device performance
    const particleCount =
      window.innerWidth < 768
        ? 2000
        : navigator.hardwareConcurrency < 4
        ? 2500
        : 3500;
    let time = 0;
    let clickPos = { x: 0, y: 0 };
    let sequenceState = "initial";
    let hasExploded = false;
    let sequenceStartTime = 0;
    let targetPositions = null;
    let orbitalParams = null;
    let orbitalMotionStartTime = null;
    let explosionPrecomputed = false;

    // Initialize Three.js scene
    scene = new THREE.Scene();

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 2000);
    camera.position.z = 500;

    // Minimal ambient light to keep black hole center dark
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
    scene.add(ambientLight);

    // Minimal lighting - black hole should be self-illuminating (emissive materials)
    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.15);
    directionalLight1.position.set(200, 200, 200);
    directionalLight1.castShadow = false; // Disabled for performance
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.08);
    directionalLight2.position.set(-200, -200, -200);
    directionalLight2.castShadow = false; // Disabled for performance
    scene.add(directionalLight2);

    // Very dim point light for subtle depth (reduced significantly)
    const pointLight = new THREE.PointLight(0xffffff, 0.2, 1000);
    pointLight.position.set(0, 0, 500);
    pointLight.castShadow = false; // Disabled for performance
    scene.add(pointLight);

    sequenceState = "initial";
    sequenceStartTime = Date.now();

    renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: false, // Disable for performance
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    renderer.shadowMap.enabled = false; // Disabled for performance
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    containerRef.current.appendChild(renderer.domElement);

    // Set up OrbitControls for interactive rotation
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enabled = false; // Disabled initially, enable when black hole appears
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = false; // Disable zoom
    controls.enablePan = false; // Disable pan
    controls.rotateSpeed = 0.5;
    controls.target.set(0, 0, 0); // Rotate around center (where black hole is)

    // Set up bloom post-processing for glowing effect
    composer = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      1.5, // strength - reduced to prevent over-brightening
      0.6, // radius - moderate spread for glow halo
      0.8 // threshold - HIGH threshold to only bloom VERY bright parts (not center!)
    );
    composer.addPass(bloomPass);

    // Create particle geometry
    geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);
    const lives = new Float32Array(particleCount);
    const velocities = new Float32Array(particleCount * 3);
    const originalPositions = new Float32Array(particleCount * 3);

    // Pre-calculate curl noise lookup table (optimized with numeric keys)
    const curlNoiseCache = new Map();
    const getCurlNoise = (px, py, pz, t) => {
      // Use numeric hash instead of string for faster Map lookups
      const ix = Math.floor(px * 0.1);
      const iy = Math.floor(py * 0.1);
      const iz = Math.floor(pz * 0.1);
      const it = Math.floor(t * 10);
      // Bit-shift to create unique numeric key (much faster than string concatenation)
      const key =
        ((ix & 0xff) << 24) |
        ((iy & 0xff) << 16) |
        ((iz & 0xff) << 8) |
        (it & 0xff);

      if (curlNoiseCache.has(key)) {
        return curlNoiseCache.get(key);
      }

      // Simple hash-based curl
      const hash = (n) => Math.sin(n * 12.9898) * 43758.5453;
      const h1 = hash(px * 0.01 + t * 0.1);
      const h2 = hash(py * 0.01 + t * 0.1);
      const h3 = hash(pz * 0.01 + t * 0.1);

      const x = (h2 - h3) * 0.5;
      const y = (h3 - h1) * 0.5;
      const z = (h1 - h2) * 0.5;

      const lenSq = x * x + y * y + z * z;
      const result =
        lenSq > 0.000001
          ? {
              x: x / Math.sqrt(lenSq),
              y: y / Math.sqrt(lenSq),
              z: z / Math.sqrt(lenSq),
            }
          : { x: 0, y: 0, z: 0 };

      // Cache with size limit
      if (curlNoiseCache.size > 2000) {
        const firstKey = curlNoiseCache.keys().next().value;
        curlNoiseCache.delete(firstKey);
      }
      curlNoiseCache.set(key, result);
      return result;
    };

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;

      // Spherical distribution
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = Math.random() * 1000;

      positions[i3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i3 + 2] = radius * Math.cos(phi);

      originalPositions[i3] = positions[i3];
      originalPositions[i3 + 1] = positions[i3 + 1];
      originalPositions[i3 + 2] = positions[i3 + 2];

      sizes[i] = Math.random() * 3 + 2.5;
      velocities[i3] = 0;
      velocities[i3 + 1] = 0;
      velocities[i3 + 2] = 0;
      lives[i] = Math.random();
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute("life", new THREE.BufferAttribute(lives, 1));

    // Create shader material
    material = new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    particleSystem = new THREE.Points(geometry, material);
    scene.add(particleSystem);

    // PRE-COMPUTE explosion pattern to avoid UI blocking on click
    const precomputeExplosion = () => {
      targetPositions = new Array(particleCount);
      orbitalParams = new Array(particleCount); // Pre-allocate instead of push

      const clickX = 0;
      const clickY = 0;

      // Pre-calculate all trig values and random values
      for (let i = 0; i < particleCount; i++) {
        const angle = (i / particleCount) * Math.PI * 2;
        const radius = 250 + Math.random() * 350;

        // Pre-compute cos/sin (expensive operations)
        const cosAngle = Math.cos(angle);
        const sinAngle = Math.sin(angle);

        const targetX = clickX + radius * cosAngle;
        const targetY = clickY + radius * sinAngle;

        // Pre-compute Z depth with simpler logic
        const zRand = Math.random();
        let targetZ;
        if (zRand < 0.2) targetZ = -200 - Math.random() * 100;
        else if (zRand < 0.5) targetZ = -100 - Math.random() * 100;
        else if (zRand < 0.8) targetZ = Math.random() * 100;
        else targetZ = 100 + Math.random() * 200;

        // Store as plain object (faster than individual properties)
        targetPositions[i] = { x: targetX, y: targetY, z: targetZ };

        // Pre-allocate orbital params
        orbitalParams[i] = {
          radius: 5 + Math.random() * 10,
          speed: 0.1 + Math.random() * 0.2,
          phase: Math.random() * Math.PI * 2,
        };
      }

      explosionPrecomputed = true;
      console.log("Explosion pattern pre-computed (no UI blocking on click)");
    };

    // Pre-compute during idle time
    precomputeExplosion();

    // Load black hole GLB model
    const loadBlackHole = () => {
      const loader = new GLTFLoader();
      loader.load(
        "/black_hole.glb",
        (gltf) => {
          // Get the black hole model from the loaded scene
          const model = gltf.scene;

          // Calculate bounding box to determine scale
          const box = new THREE.Box3().setFromObject(model);
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);

          // Scale to desired size - make it WAY smaller (0.05 = 5% of bounding box size)
          const desiredSize = 300;
          const baseScale = desiredSize / maxDim;

          // Store the base scale BEFORE applying it
          model.userData.baseScale = baseScale;

          // Apply scale directly to the model
          model.scale.set(baseScale, baseScale, baseScale);

          // Recalculate bounding box after scaling
          box.setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());

          // Center the model at origin (where particles disappear after explosion)
          model.position.set(-center.x, -center.y, -center.z);

          // Make it invisible initially
          model.visible = false;

          // Create gradient glow effect: bright ring, darker center (like original)
          console.log("Processing black hole model for gradient glow...");

          model.traverse((child) => {
            if (child.isMesh) {
              // Shadows already disabled globally for performance

              // Calculate distance from center to determine glow intensity
              // Get bounding box center of this mesh
              const meshBox = new THREE.Box3().setFromObject(child);
              const meshCenter = meshBox.getCenter(new THREE.Vector3());
              const distanceFromOrigin = Math.sqrt(
                meshCenter.x * meshCenter.x +
                  meshCenter.y * meshCenter.y +
                  meshCenter.z * meshCenter.z
              );

              // Get the actual size of the model to calculate proper distances
              const modelBox = new THREE.Box3().setFromObject(model);
              const modelSize = modelBox.getSize(new THREE.Vector3());
              const maxDistance =
                Math.max(modelSize.x, modelSize.y, modelSize.z) * 0.5;

              // Calculate normalized distance (0 = center, 1 = edge)
              const normalizedDist = Math.min(
                1.0,
                distanceFromOrigin / maxDistance
              );

              // Glow intensity: high at edges (ring), low at center
              // Use inverse curve - center is dark, edges are bright
              // This creates the glowing ring effect

              // Check mesh name for identification (order matters!)
              const meshName = child.name.toLowerCase();
              const isRing = meshName.includes("ring");
              const isLight = meshName.includes("light"); // Light meshes should glow
              const isPlanet = meshName.includes("planet");

              // Only mark as center if NOT a ring, light, or planet
              const isCenter =
                !isRing &&
                !isLight &&
                !isPlanet &&
                (meshName.includes("center") ||
                  meshName.includes("blackoutside") ||
                  meshName.includes("distortion") ||
                  normalizedDist < 0.15); // Only innermost 15% by distance

              if (child.material) {
                const materials = Array.isArray(child.material)
                  ? child.material
                  : [child.material];

                materials.forEach((mat, index) => {
                  let newMaterial;

                  // Determine glow based on mesh name (more reliable than distance)
                  // Realistic black hole: bright orange/yellow accretion disk, blue jets
                  let finalGlowIntensity;
                  let glowColor;

                  if (isRing) {
                    // Ring meshes = accretion disk - SUPER BRIGHT orange/yellow
                    finalGlowIntensity = 6.0; // Extremely bright for dramatic effect
                    glowColor = new THREE.Color(0xff8800); // Orange glow (accretion disk)
                  } else if (isLight) {
                    // Light meshes = jets/bright areas - VERY BRIGHT blue/white
                    finalGlowIntensity = 5.0; // Very bright for light elements
                    glowColor = new THREE.Color(0x88ccff); // Blue-white glow (jets)
                  } else if (isCenter) {
                    // Center/blackoutside/distortion = event horizon - PITCH BLACK
                    finalGlowIntensity = 0.0; // No glow - pure darkness
                    glowColor = new THREE.Color(0x000000); // Pure black
                  } else if (isPlanet) {
                    // Planet mesh - moderate warm glow
                    finalGlowIntensity = 2.0;
                    glowColor = new THREE.Color(0xffaa44); // Warm orange
                  } else {
                    // Unknown meshes - check distance, inner parts should be darker
                    if (normalizedDist < 0.4) {
                      // Inner region - should be dark
                      finalGlowIntensity = 0.0;
                      glowColor = new THREE.Color(0x000000);
                    } else if (normalizedDist < 0.7) {
                      // Middle region - moderate glow
                      finalGlowIntensity = 1.5;
                      glowColor = new THREE.Color(0xff6622);
                    } else {
                      // Outer region - brighter glow
                      finalGlowIntensity = 3.0;
                      glowColor = new THREE.Color(0xff8844);
                    }
                  }

                  // Log mesh details BEFORE material application
                  const matType = isCenter
                    ? "MeshBasic (NO LIGHTS)"
                    : "MeshStandard (LIGHTS)";
                  console.log(
                    `✨ ${
                      child.name
                    }: Center=${isCenter}, Ring=${isRing}, Light=${isLight}, Glow=${finalGlowIntensity.toFixed(
                      1
                    )}, WillUse=${matType}`
                  );

                  if (mat && mat.type === "ShaderMaterial") {
                    const originalColor = mat.color
                      ? new THREE.Color(mat.color)
                      : glowColor;

                    // Use MeshBasicMaterial for center (doesn't respond to lights)
                    if (isCenter) {
                      newMaterial = new THREE.MeshBasicMaterial({
                        color: new THREE.Color(0x000000), // Pure black
                        transparent: mat.transparent || false,
                        opacity: mat.opacity !== undefined ? mat.opacity : 1.0,
                        side: THREE.FrontSide,
                      });
                    } else {
                      newMaterial = new THREE.MeshStandardMaterial({
                        color: isRing || isLight ? glowColor : originalColor,
                        emissive: glowColor,
                        emissiveIntensity: finalGlowIntensity,
                        metalness: 0.2,
                        roughness: 0.2,
                        transparent: mat.transparent || false,
                        opacity: mat.opacity !== undefined ? mat.opacity : 1.0,
                        side: THREE.DoubleSide,
                      });
                    }

                    // Store base intensity for pulsing animation (but never pulse the center)
                    newMaterial.userData.baseEmissiveIntensity = isCenter
                      ? 0.0
                      : finalGlowIntensity;
                    newMaterial.userData.isBlackHole = isCenter; // Mark for bloom exclusion

                    if (mat.map) {
                      newMaterial.map = mat.map;
                      newMaterial.map.needsUpdate = true;
                    }
                    if (mat.emissiveMap && !isCenter) {
                      newMaterial.emissiveMap = mat.emissiveMap;
                      newMaterial.emissiveMap.needsUpdate = true;
                      const boostedIntensity = finalGlowIntensity * 2.0;
                      newMaterial.emissiveIntensity = boostedIntensity;
                      newMaterial.userData.baseEmissiveIntensity =
                        boostedIntensity;
                    }
                  } else if (mat) {
                    if (
                      mat.isMeshStandardMaterial ||
                      mat.isMeshPhysicalMaterial
                    ) {
                      // For center meshes, convert to MeshBasicMaterial (no light response)
                      if (isCenter) {
                        newMaterial = new THREE.MeshBasicMaterial({
                          color: new THREE.Color(0x000000), // Pure black
                          transparent: mat.transparent || false,
                          opacity:
                            mat.opacity !== undefined ? mat.opacity : 1.0,
                          side: THREE.FrontSide,
                        });
                        newMaterial.userData.baseEmissiveIntensity = 0.0;
                        newMaterial.userData.isBlackHole = true;
                      } else {
                        // Create new MeshStandardMaterial for glowing parts
                        const originalColor = mat.color
                          ? new THREE.Color(mat.color)
                          : glowColor;
                        newMaterial = new THREE.MeshStandardMaterial({
                          color: originalColor,
                          emissive: glowColor,
                          emissiveIntensity: finalGlowIntensity,
                          metalness: 0.2,
                          roughness: 0.3,
                          transparent: mat.transparent || false,
                          opacity:
                            mat.opacity !== undefined ? mat.opacity : 1.0,
                          side: THREE.DoubleSide,
                        });

                        // Store base intensity for pulsing animation
                        newMaterial.userData.baseEmissiveIntensity =
                          finalGlowIntensity;
                        newMaterial.userData.isBlackHole = false;
                      }

                      // Apply realistic black hole colors to newMaterial (only for non-center meshes)
                      if (newMaterial && newMaterial.color && !isCenter) {
                        if (isRing) {
                          // Accretion disk - bright orange/yellow
                          newMaterial.color.setRGB(1.0, 0.6, 0.2);
                        } else if (isLight) {
                          // Jets/light areas - bright blue-white
                          newMaterial.color.setRGB(0.7, 0.9, 1.0);
                        } else if (isPlanet) {
                          // Planet - warm tones
                          newMaterial.color.setRGB(1.0, 0.8, 0.4);
                        } else if (normalizedDist < 0.4) {
                          // Inner dark region - very dark
                          newMaterial.color.setRGB(0.1, 0.05, 0.0);
                        } else if (normalizedDist < 0.7) {
                          // Middle region - darker orange/red
                          newMaterial.color.setRGB(0.8, 0.3, 0.1);
                        } else {
                          // Outer parts - bright orange gradient
                          newMaterial.color.setRGB(1.0, 0.5, 0.2);
                        }
                      }

                      // Copy texture maps to newMaterial
                      if (newMaterial && !isCenter) {
                        if (mat.map) {
                          newMaterial.map = mat.map;
                          newMaterial.map.needsUpdate = true;
                        }
                        if (mat.emissiveMap) {
                          newMaterial.emissiveMap = mat.emissiveMap;
                          newMaterial.emissiveMap.needsUpdate = true;
                          const boostedIntensity = finalGlowIntensity * 2.5;
                          newMaterial.emissiveIntensity = boostedIntensity;
                          newMaterial.userData.baseEmissiveIntensity =
                            boostedIntensity;
                        }
                      }
                    } else {
                      // For other material types
                      if (isCenter) {
                        // Center uses MeshBasicMaterial (no light response)
                        newMaterial = new THREE.MeshBasicMaterial({
                          color: new THREE.Color(0x000000),
                          transparent: mat.transparent || false,
                          opacity:
                            mat.opacity !== undefined ? mat.opacity : 1.0,
                          side: THREE.FrontSide,
                        });
                        newMaterial.userData.baseEmissiveIntensity = 0.0;
                        newMaterial.userData.isBlackHole = true;
                      } else {
                        // Rings/lights/planets use MeshStandardMaterial (responds to lights)
                        const originalColor = mat.color
                          ? new THREE.Color(mat.color)
                          : glowColor;
                        newMaterial = new THREE.MeshStandardMaterial({
                          color: isRing || isLight ? glowColor : originalColor,
                          emissive: glowColor,
                          emissiveIntensity: finalGlowIntensity,
                          metalness: 0.2,
                          roughness: 0.3,
                          side: THREE.DoubleSide,
                        });
                        newMaterial.userData.baseEmissiveIntensity =
                          finalGlowIntensity;
                        newMaterial.userData.isBlackHole = false;
                      }

                      if (mat.map) {
                        newMaterial.map = mat.map;
                        newMaterial.map.needsUpdate = true;
                      }

                      if (mat.emissiveMap && !isCenter) {
                        newMaterial.emissiveMap = mat.emissiveMap;
                        newMaterial.emissiveMap.needsUpdate = true;
                        const boostedIntensity = finalGlowIntensity * 2.5;
                        newMaterial.emissiveIntensity = boostedIntensity;
                        newMaterial.userData.baseEmissiveIntensity =
                          boostedIntensity;
                      }
                    }
                  }

                  if (newMaterial) {
                    if (Array.isArray(child.material)) {
                      child.material[index] = newMaterial;
                    } else {
                      child.material = newMaterial;
                    }
                    console.log(
                      `   ✅ Material replaced: ${newMaterial.type}, Color: ${
                        newMaterial.color
                          ? newMaterial.color.getHexString()
                          : "N/A"
                      }, Emissive: ${newMaterial.emissiveIntensity || 0}`
                    );
                  }
                });
              }
            }
          });

          blackHole = model;
          scene.add(blackHole);

          console.log("Black hole loaded with realistic glow effects");

          // If explosion already happened, show the black hole now
          if (shouldShowBlackHole) {
            blackHole.visible = true;
            if (controls) controls.enabled = true; // Enable rotation controls
            const targetScale = blackHole.userData.baseScale || 0.05;
            blackHole.scale.set(0, 0, 0);
            // Animate black hole appearance
            const animateBlackHoleIn = () => {
              if (blackHole && blackHole.scale.x < targetScale) {
                const increment = targetScale * 0.05;
                blackHole.scale.x = Math.min(
                  blackHole.scale.x + increment,
                  targetScale
                );
                blackHole.scale.y = Math.min(
                  blackHole.scale.y + increment,
                  targetScale
                );
                blackHole.scale.z = Math.min(
                  blackHole.scale.z + increment,
                  targetScale
                );
                requestAnimationFrame(animateBlackHoleIn);
              }
            };
            animateBlackHoleIn();
          }
        },
        (progress) => {
          // Loading progress (optional)
          console.log(
            "Loading black hole model:",
            (progress.loaded / progress.total) * 100 + "%"
          );
        },
        (error) => {
          console.error("Error loading black hole model:", error);
        }
      );
    };

    loadBlackHole();

    // Event listeners
    const handleMouseMove = (event) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const normalizedX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const normalizedY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      mouseRef.current.x = normalizedX * 1000;
      mouseRef.current.y = normalizedY * 1000;
      mouseRef.current.z = 0;
    };

    const handleClick = (event) => {
      if (!containerRef.current || hasExploded || sequenceState !== "ready")
        return;

      hasExploded = true;
      sequenceState = "exploded";
      orbitalMotionStartTime = Date.now();

      clickPos.x = 0;
      clickPos.y = 0;

      window.dispatchEvent(new CustomEvent("particleExplosion"));

      // Show black hole after explosion
      shouldShowBlackHole = true;
      if (blackHole) {
        blackHole.visible = true;
        if (controls) controls.enabled = true; // Enable rotation controls
        const targetScale = blackHole.userData.baseScale || 0.05;
        blackHole.scale.set(0, 0, 0);
        // Animate black hole appearance
        const animateBlackHoleIn = () => {
          if (blackHole && blackHole.scale.x < targetScale) {
            const increment = targetScale * 0.05;
            blackHole.scale.x = Math.min(
              blackHole.scale.x + increment,
              targetScale
            );
            blackHole.scale.y = Math.min(
              blackHole.scale.y + increment,
              targetScale
            );
            blackHole.scale.z = Math.min(
              blackHole.scale.z + increment,
              targetScale
            );
            requestAnimationFrame(animateBlackHoleIn);
          }
        };
        animateBlackHoleIn();
      }

      // Use pre-computed explosion data - NO EXPENSIVE CALCULATIONS!
      // Just set velocities based on current positions and pre-computed targets
      if (explosionPrecomputed && targetPositions) {
        const speed = 20;
        for (let i = 0; i < particleCount; i++) {
          const i3 = i * 3;
          const target = targetPositions[i];

          const dx = target.x - positions[i3];
          const dy = target.y - positions[i3 + 1];
          const dz = target.z - positions[i3 + 2];
          const distSq = dx * dx + dy * dy + dz * dz;

          if (distSq > 0) {
            const invDist = 1.0 / Math.sqrt(distSq);
            velocities[i3] = dx * invDist * speed;
            velocities[i3 + 1] = dy * invDist * speed;
            velocities[i3 + 2] = dz * invDist * speed;
          }
        }
      }
    };

    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);

      // Update composer size
      if (composer) {
        composer.setSize(width, height);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("click", handleClick);
    window.addEventListener("resize", handleResize);

    // Optimized animation loop
    const animate = () => {
      animationId = requestAnimationFrame(animate);

      time += 0.016;

      // Update sequence state
      const elapsed = (Date.now() - sequenceStartTime) / 1000;
      if (sequenceState === "initial" && elapsed > 2) {
        sequenceState = "attracting";
        window.dispatchEvent(new CustomEvent("particleAttracting"));
      } else if (sequenceState === "attracting" && elapsed > 5) {
        sequenceState = "ready";
        window.dispatchEvent(new CustomEvent("particleReady"));
      }

      // Cache mouse position and distance squared
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const mz = mouseRef.current.z;
      const mouseDistSqThreshold = 500 * 500; // Squared distance

      // Cache sequence state checks
      const isAttracting = sequenceState === "attracting";
      const isReady = sequenceState === "ready" && !hasExploded;

      // Update particles - optimized loop
      const shouldOrbit =
        hasExploded &&
        orbitalMotionStartTime &&
        Date.now() >= orbitalMotionStartTime;
      const orbitTime = shouldOrbit
        ? (Date.now() - orbitalMotionStartTime) / 1000
        : 0;
      const maxRadiusSq = 1000 * 1000; // Squared for comparison

      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;

        let px = positions[i3];
        let py = positions[i3 + 1];
        let pz = positions[i3 + 2];

        if (hasExploded && targetPositions && targetPositions[i]) {
          const target = targetPositions[i];

          // Use squared distance to avoid sqrt
          const dx = px - target.x;
          const dy = py - target.y;
          const dz = pz - target.z;
          const distToTargetSq = dx * dx + dy * dy + dz * dz;

          // Orbital motion
          if (
            shouldOrbit &&
            orbitalParams &&
            orbitalParams[i] &&
            distToTargetSq < 900
          ) {
            // 30^2
            const orbit = orbitalParams[i];
            const currentAngle = orbit.phase + orbitTime * orbit.speed;

            positions[i3] = target.x + orbit.radius * Math.cos(currentAngle);
            positions[i3 + 1] =
              target.y + orbit.radius * Math.sin(currentAngle);
            positions[i3 + 2] =
              target.z + orbit.radius * 0.3 * Math.sin(currentAngle * 2);

            velocities[i3] = 0;
            velocities[i3 + 1] = 0;
            velocities[i3 + 2] = 0;
            continue;
          }

          // Still moving to target
          if (distToTargetSq < 400) {
            // 20^2
            positions[i3] = target.x;
            positions[i3 + 1] = target.y;
            positions[i3 + 2] = target.z;
            velocities[i3] = 0;
            velocities[i3 + 1] = 0;
            velocities[i3 + 2] = 0;
            continue;
          }

          // Reduced damping for longer travel - particles coast longer before braking
          if (distToTargetSq < 2500) {
            // 50^2 - close to target, start braking
            velocities[i3] *= 0.88; // Slightly less aggressive braking
            velocities[i3 + 1] *= 0.88;
            velocities[i3 + 2] *= 0.88;
          } else if (distToTargetSq < 10000) {
            // 100^2 - medium distance, light braking
            velocities[i3] *= 0.97;
            velocities[i3 + 1] *= 0.97;
            velocities[i3 + 2] *= 0.97;
          } else {
            // Far from target - minimal damping, let them coast
            velocities[i3] *= 0.99;
            velocities[i3 + 1] *= 0.99;
            velocities[i3 + 2] *= 0.99;
          }
        } else if (!hasExploded) {
          // Before explosion: apply forces
          let fx = 0,
            fy = 0,
            fz = 0;

          // Simplified curl noise (cached)
          const curl = getCurlNoise(px, py, pz, time);
          let curlStrength = 0.05;

          if (isAttracting) {
            curlStrength = 0.1;
            // Gravity to center (optimized with inverse distance)
            const distFromCenterSq = px * px + py * py + pz * pz;
            if (distFromCenterSq > 0.001) {
              const distFromCenter = Math.sqrt(distFromCenterSq);
              const gravityStrength = 0.015;
              const invDist = 1.0 / distFromCenter;
              fx -= px * invDist * gravityStrength;
              fy -= py * invDist * gravityStrength;
              fz -= pz * invDist * gravityStrength;
            }
          } else if (isReady) {
            curlStrength = 0.05;
            // Weak gravity (optimized with inverse distance)
            const distFromCenterSq = px * px + py * py + pz * pz;
            if (distFromCenterSq > 0.001) {
              const distFromCenter = Math.sqrt(distFromCenterSq);
              const gravityStrength = 0.01;
              const invDist = 1.0 / distFromCenter;
              fx -= px * invDist * gravityStrength;
              fy -= py * invDist * gravityStrength;
              fz -= pz * invDist * gravityStrength;
            }
          }

          fx += curl.x * curlStrength;
          fy += curl.y * curlStrength;
          fz += curl.z * curlStrength;

          // Mouse interaction (use squared distance)
          const mdx = mx - px;
          const mdy = my - py;
          const mdz = mz - pz;
          const mouseDistSq = mdx * mdx + mdy * mdy + mdz * mdz;

          if (mouseDistSq > 0.001 && mouseDistSq < mouseDistSqThreshold) {
            const mouseDist = Math.sqrt(mouseDistSq);
            const mouseInfluence = 1.0 / (1.0 + mouseDist * 0.01);
            const invMouseDist = 1.0 / mouseDist;
            fx += mdx * invMouseDist * mouseInfluence * 0.3;
            fy += mdy * invMouseDist * mouseInfluence * 0.3;
            fz += mdz * invMouseDist * mouseInfluence * 0.3;
          }

          // Update velocity
          velocities[i3] += fx * 0.05;
          velocities[i3 + 1] += fy * 0.05;
          velocities[i3 + 2] += fz * 0.05;

          // Damping
          velocities[i3] *= 0.98;
          velocities[i3 + 1] *= 0.98;
          velocities[i3 + 2] *= 0.98;
        } else {
          // Fallback damping
          velocities[i3] *= 0.9;
          velocities[i3 + 1] *= 0.9;
          velocities[i3 + 2] *= 0.9;
        }

        // Update position
        positions[i3] += velocities[i3];
        positions[i3 + 1] += velocities[i3 + 1];
        positions[i3 + 2] += velocities[i3 + 2];

        // Spherical boundary (use squared distance)
        const distFromCenterSq =
          positions[i3] * positions[i3] +
          positions[i3 + 1] * positions[i3 + 1] +
          positions[i3 + 2] * positions[i3 + 2];

        if (distFromCenterSq > maxRadiusSq) {
          const distFromCenter = Math.sqrt(distFromCenterSq);
          const excess = distFromCenter - 1000.0;
          const damping = 1.0 - excess * 0.01; // Optimized division
          const dampingFactor = Math.max(0.1, damping);

          velocities[i3] *= dampingFactor;
          velocities[i3 + 1] *= dampingFactor;
          velocities[i3 + 2] *= dampingFactor;

          if (distFromCenter > 0.001) {
            const scale = 1000.0 / distFromCenter;
            positions[i3] *= scale;
            positions[i3 + 1] *= scale;
            positions[i3 + 2] *= scale;
          }
        }
      }

      // Throttle buffer updates for performance (update every frame for smooth motion)
      // For extreme performance boost, change to: frameCount % 2 === 0
      // frameCount++;
      geometry.attributes.position.needsUpdate = true;

      // Update black hole animation (no auto-rotation, using controls instead)
      if (blackHole && blackHole.visible) {
        // Auto-rotation disabled - using OrbitControls for interactive rotation

        // Add subtle pulsing glow for realistic effect
        const pulseSpeed = time * 0.5;
        const pulseAmount = Math.sin(pulseSpeed) * 0.3 + 1.0; // Range: 0.7 to 1.3

        // Update emissive intensity for pulsing effect (but skip black hole center)
        blackHole.traverse((child) => {
          if (child.isMesh && child.material) {
            const materials = Array.isArray(child.material)
              ? child.material
              : [child.material];
            materials.forEach((mat) => {
              // Only pulse if it's NOT the black hole center and has emissive properties
              if (
                mat.emissive &&
                mat.userData.baseEmissiveIntensity !== undefined &&
                !mat.userData.isBlackHole
              ) {
                // Pulse the glow (accretion disk and jets only)
                mat.emissiveIntensity =
                  mat.userData.baseEmissiveIntensity * pulseAmount;
              }
            });
          }
        });
      }

      // Update controls for smooth damping
      if (controls && controls.enabled) {
        controls.update();
      }

      // Use composer for bloom effect, or fallback to regular renderer
      if (composer) {
        composer.render();
      } else {
        renderer.render(scene, camera);
      }
    };

    animate();

    // Cleanup function
    const currentContainer = containerRef.current;
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("click", handleClick);
      window.removeEventListener("resize", handleResize);

      if (animationId) {
        cancelAnimationFrame(animationId);
      }

      if (controls) {
        controls.dispose();
      }

      if (currentContainer && renderer && renderer.domElement) {
        try {
          currentContainer.removeChild(renderer.domElement);
        } catch (e) {
          // Element might already be removed
        }
      }

      if (renderer) renderer.dispose();
      if (material) material.dispose();
      if (geometry) geometry.dispose();
      if (blackHole) {
        // Dispose of GLB model resources
        blackHole.traverse((child) => {
          if (child.isMesh) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach((material) => material.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
        scene.remove(blackHole);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "auto",
        zIndex: 1,
      }}
    />
  );
};

export default ParticleSystem;
